import React from 'react';
import { CardCustomizationPane } from './components/CardCustomizationContent/CardCustomizationPane';
import { CardPreviewPane } from './components/CardCustomizationContent/CardPreviewPane';
import { PdpLayout } from './components/PdpLayout/PdpLayout';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { usePriceSummary } from '@apps/card/components/PriceSummary/usePriceSummary';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useFreeShippingCountries } from './useFreeShippingCountries';
import type { BasePdpImplementationProps } from './ProductDetailsPage.types';
import type { StationeryTemplatePDP } from './ProductDetailsPage.types';
import type { DraftData } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { MinimalRequiredTemplateDataForDraft } from '@apps/card/types/stationeryTemplate.types';
import { usePdpColorways } from './PdpColorwayContext';
import { withSelectedColorway } from './utils';

type PaperPdpProps = BasePdpImplementationProps &
  Readonly<{
    template: StationeryTemplatePDP;
    onPaperCustomization: (draft: DraftData) => void;
    onCustomizeClick: (template: MinimalRequiredTemplateDataForDraft, subtotalInMinorUnits?: number) => void;
  }>;

export const PaperPdp = (props: PaperPdpProps) => {
  const {
    productSelector,
    intersectionRef,
    isCreatingDraft,
    onCustomizeClick,
    onPaperCustomization,
    showCustomizeButtonInHeader,
    template,
    themeId,
    designName,
    templateCategory,
    eventId,
    isFoil,
  } = props;

  const { options, selectedColorwayId } = usePdpColorways();

  const themeJson = template.themeJSON;

  const isMobileOrTablet = useIsMobileOrTablet();

  const { total, totalInMinorUnits } = usePriceSummary({ templateCategory, priceUpTo: 'cardFront', cardConfig: template.themeJSON });
  const { activeProduct } = useStationeryProductPricingContext();
  const countriesWithFreeShipping = useFreeShippingCountries([activeProduct]);

  // Bake the selected colorway into the template so the created draft opens on
  // the chosen variant.
  const handleOnCustomizeNowClick = useEventCallback(() => {
    onCustomizeClick(withSelectedColorway(template, options, selectedColorwayId ?? ''), totalInMinorUnits);
  });

  return (
    <PdpLayout
      themeId={themeId}
      designName={designName}
      templateCategory={templateCategory}
      isPremium={template.premium}
      isFoil={isFoil}
      selectedColorwayId={selectedColorwayId}
      onCustomizeNowClick={handleOnCustomizeNowClick}
      isCreatingDraft={isCreatingDraft}
      productSelector={productSelector}
      subtotal={total}
      showCustomizeButtonInHeader={showCustomizeButtonInHeader}
      previewPane={() => (
        <CardPreviewPane
          eventId={eventId}
          templateId={template.id}
          isFavorite={template.isFavorite}
          themeId={template.themeId}
          templateCategory={templateCategory}
          cardFront={themeJson.card.front}
          cardShape={themeJson.customizations.shape}
          thumbnailUrl={template.thumbnailUrl!}
          cardJson={template.themeJSON}
        />
      )}
      customizationPane={() => (
        <CardCustomizationPane
          hideCustomizeNowButton={isMobileOrTablet}
          intersectionRef={intersectionRef}
          customizationConfig={themeJson}
          isCreatingDraft={isCreatingDraft}
          countriesWithFreeShipping={countriesWithFreeShipping}
          templateId={template.id}
          themeId={themeId}
          themeName={designName}
          stationeryTemplateCategory={templateCategory}
          onCardCustomizeClick={handleOnCustomizeNowClick}
          onCardCustomization={onPaperCustomization}
        />
      )}
    />
  );
};
